import { useEffect, useState } from 'react';
import { Card, List, Button, Avatar, Typography, Space, Popconfirm, Tag, Tooltip } from 'antd';
import {
  CircleCheck as CircleCheckIcon,
  Link2 as Link2Icon,
  TriangleAlert as TriangleAlertIcon,
  Unlink as UnlinkIcon,
} from 'lucide-react';
import { EmptyState } from '@/components/common/EmptyState';
import { useTranslation } from 'react-i18next';
import { useNavigate } from 'react-router';
import { useWalletConnect } from '@/hooks/useWalletConnect';
import { isEvmChain } from '@/types/vault';
import { vaultsApi } from '@/api/vaults';
import { ConnectDappButton } from './ConnectDappButton';
import { resolveDappIdentity, type DappVerification } from '@/lib/walletconnect/verify';
import type { WcVaultBinding } from '@/types/walletconnect';

const { Text, Link } = Typography;

const VERIFY_TAG_COLOR: Record<DappVerification, string> = {
  verified: 'green',
  mismatch: 'red',
  unverified: 'orange',
  scam: 'red',
};

interface Props {
  orgId: string;
  vaultId: string;
  vaultName: string;
  chain: string;
  /** EVM address of the vault (address index 0) exposed to dApps. */
  address?: string;
  addressIndex?: number;
}

/**
 * Per-vault WalletConnect panel: connect this vault's treasury to a dApp and
 * manage active dApp sessions. dApp transactions become M-of-N proposals.
 * Rendered only for EVM vaults (WalletConnect is EVM-only here).
 */
export function VaultWalletConnectCard({
  orgId,
  vaultId,
  vaultName,
  chain,
  address,
  addressIndex = 0,
}: Props) {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const wc = useWalletConnect();

  // Signing-readiness: an undeployed account can't answer ERC-1271 (message
  // signing). `undefined` = unknown (checking / RPC unavailable / non-EVM).
  const [deployed, setDeployed] = useState<boolean | undefined>(undefined);
  const isEvm = isEvmChain(chain);
  useEffect(() => {
    if (!isEvm || !address) return;
    let cancelled = false;
    vaultsApi
      .getSignatureDeploymentStatus(orgId, vaultId, addressIndex)
      .then((r) => {
        if (!cancelled) setDeployed(r.deployed);
      })
      .catch(() => {
        /* unknown — leave undefined */
      });
    return () => {
      cancelled = true;
    };
  }, [isEvm, address, orgId, vaultId, addressIndex]);

  // WalletConnect is EVM-only and needs the vault's EVM address.
  if (!isEvm || !address || !wc) return null;

  const deploymentBadge =
    deployed === false ? (
      <Tooltip title={t('walletconnect.card.not_deployed_hint')}>
        <Tag icon={<TriangleAlertIcon />} color="warning">
          {t('walletconnect.card.not_deployed')}
        </Tag>
      </Tooltip>
    ) : deployed === true ? (
      <Tooltip title={t('walletconnect.card.deployed_hint')}>
        <Tag icon={<CircleCheckIcon />} color="success">
          {t('walletconnect.card.deployed')}
        </Tag>
      </Tooltip>
    ) : null;

  const binding: WcVaultBinding = {
    orgId,
    vaultId,
    vaultName,
    chain,
    address,
    addressIndex,
  };

  // Sessions bound to this vault.
  const topics = Object.entries(wc.bindings)
    .filter(([, b]) => b.vaultId === vaultId)
    .map(([topic]) => topic);
  const sessions = wc.activeSessions.filter((s) => topics.includes(s.topic));

  return (
    <Card
      title={t('walletconnect.card.title')}
      extra={
        <Space>
          {deploymentBadge}
          <ConnectDappButton binding={binding} />
        </Space>
      }
    >
      {sessions.length === 0 ? (
        <EmptyState icon={<Link2Icon />} title={t('walletconnect.card.empty')} />
      ) : (
        <List
          dataSource={sessions}
          renderItem={(session) => {
            const meta = session.peer.metadata;
            // Show the Verify-attested origin captured at approval; the
            // self-declared name is secondary (ENTX-A-4).
            const identity = resolveDappIdentity(wc.bindings[session.topic]?.verifyContext, meta);
            const icons: unknown = meta.icons;
            const icon =
              Array.isArray(icons) &&
              typeof icons[0] === 'string' &&
              icons[0].startsWith('https://')
                ? icons[0]
                : undefined;
            return (
              <List.Item
                actions={[
                  <Popconfirm
                    key="disconnect"
                    title={t('walletconnect.card.disconnect_confirm')}
                    okText={t('walletconnect.card.disconnect')}
                    onConfirm={() => void wc.disconnect(session.topic)}
                  >
                    <Button
                      danger
                      type="text"
                      size="small"
                      icon={<UnlinkIcon />}
                      aria-label={t('walletconnect.card.disconnect')}
                    />
                  </Popconfirm>,
                ]}
              >
                <List.Item.Meta
                  avatar={
                    <Avatar src={icon} shape="square">
                      {identity.claimedName.charAt(0) || '?'}
                    </Avatar>
                  }
                  title={
                    <Space size={4} wrap>
                      <Text strong className="break-all">
                        {identity.origin ?? t('walletconnect.verify.unknown_origin')}
                      </Text>
                      <Tag color={VERIFY_TAG_COLOR[identity.verification]}>
                        {t(`walletconnect.verify.${identity.verification}`)}
                      </Tag>
                    </Space>
                  }
                  description={
                    identity.claimedName ? (
                      <Text type="secondary" style={{ fontSize: 12 }}>
                        {t('walletconnect.verify.claims', {
                          name: identity.claimedName,
                          url: identity.claimedOrigin ?? '—',
                        })}
                      </Text>
                    ) : null
                  }
                />
              </List.Item>
            );
          }}
        />
      )}
      <div style={{ marginTop: 12, textAlign: 'right' }}>
        <Link onClick={() => navigate(`/vaults/${vaultId}/signature-requests`)}>
          {t('walletconnect.sign.list_title')} →
        </Link>
      </div>
    </Card>
  );
}
